import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { LSPersistenceKeys } from 'app/core/constants';
import { LocalStorageService } from 'app/services/localStorage.service';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';

import { EFFECTS_DISABLED, socialAppActions, VideoEffect } from '../state/app';

const EFFECTS_VERSION = 1;
interface VideoEffectState {
  version: number;
  effect: VideoEffect;
}

const rehydratedEffect = (value: string): VideoEffectState => {
  const rawObject = JSON.parse(value);
  if (rawObject.version !== EFFECTS_VERSION) {
    throw new Error('Invalid video effect version');
  }
  return rawObject;
};

@Injectable({
  providedIn: 'root',
})
export class VideoEffectService extends Loggable {
  private localStorageService = inject(LocalStorageService);
  private store = inject<Store<AppState>>(Store);

  public constructor() {
    super();
    this.setDisplayName('VideoEffectService');
  }

  public init() {
    try {
      const { effect, version } = this.localStorageService.retrieve<VideoEffectState>(
        LSPersistenceKeys.VIDEO_EFFECT,
        rehydratedEffect,
      ) ?? { version: 0, effect: EFFECTS_DISABLED };
      if (version === EFFECTS_VERSION) {
        this.store.dispatch(socialAppActions.APPLY_VIDEO_EFFECTS({ effect }));
      }
    } catch (e) {
      this.error('Failed to retrieve video effect from local storage', e);
    }
  }

  public storeEffect(effect: VideoEffect) {
    try {
      this.localStorageService.store(LSPersistenceKeys.VIDEO_EFFECT, { version: EFFECTS_VERSION, effect });
    } catch (e) {
      this.error('Failed to store video effect in local storage', e);
    }
  }

  public applyEffect(effect: VideoEffect) {
    this.storeEffect(effect);
    this.store.dispatch(socialAppActions.APPLY_VIDEO_EFFECTS({ effect }));
  }
}
